/**
 * Qiyas Design Identity — canonical tokens (v1.1, locked 2026-07-22).
 *
 * THE single source of truth for the portal's visual system ("classic chic":
 * heritage-desk register — warm ledger stock, ink, one teal signature accent).
 * muiTheme.ts, getWorkstationTokens() and terminalStyles.ts all derive from
 * this file; pages must never hardcode raw hex that has a token here.
 *
 * Reference: the published "Qiyas — Design ID" artifact +
 * .agent-os/reviews/2026-07-22-qiyas-design-system-audit.md
 *
 * Discipline rules (canonical):
 *  1. The accent never colors a figure — numbers are ink, up or down.
 *  2. P&L colors never color chrome.
 *  3. Light-mode interactive uses accent.deep (AA); dark-mode uses accent.core.
 */

export const ACCENT = {
  /** the signature anchor — graphic fills, chart series 1, dark-mode interactive */
  core: '#5FA892',
  /** light-mode interactive (text / CTA / focus) — ~5.7:1 on the light surface */
  deep: '#2E6B5B',
  /** darker pressed/hover companion of deep */
  deeper: '#1F4A3F',
  /** hover / selection wash */
  tint: 'rgba(95, 168, 146, 0.14)',
  tintStrong: 'rgba(95, 168, 146, 0.28)',
  line: 'rgba(95, 168, 146, 0.45)',
} as const;

export const LIGHT = {
  ground: '#EAE8E0', // page field — ledger bone
  surface: '#F7F5EF', // panels & cards
  surface2: '#FCFBF6', // inputs, raised paper
  panel: '#F0EEE5', // workstation panel (between ground and surface)
  header: '#E3DFD2', // table/section header parchment
  toolbar: '#EFECE2',
  ink: '#1B2230', // chrome, headings, primary text
  inkSoft: '#3F4655', // secondary text / labels
  muted: '#726B5B', // tertiary / captions
  rule: '#D6D1C4', // hairline structure
  ruleStrong: '#C2BCAD',
  inputBorder: '#B5AF9E',
  highlight: '#F6F0D8', // editable-emphasis wash (was the terminal yellow)
  up: '#3E7A3E', // buy · gain · pass (true green, kept off the accent hue)
  down: '#9E3A3A', // sell · loss · fail (oxblood)
  warn: '#8A6A33', // antique brass
  info: '#46698C', // muted steel
} as const;

export const DARK = {
  ground: '#14181F', // warm ink night — NOT blue-navy
  surface: '#1C222C',
  surface2: '#232A35',
  input: '#171D26',
  ink: '#ECE8DE',
  inkSoft: '#BDB7A9',
  muted: '#8B8574',
  rule: '#323944',
  ruleStrong: '#414A57',
  accent: ACCENT.core, // dark-mode interactive IS the anchor
  up: '#7FBF7A',
  down: '#D98D8D',
  warn: '#CBA867',
  info: '#7E9DBE',
} as const;

export const FONTS = {
  /** book serif — page & section titles (h1–h4). Zero-payload system stack. */
  serif:
    '"Iowan Old Style", "Palatino Linotype", Palatino, "Book Antiqua", "Hoefler Text", Georgia, serif',
  /** humanist sans — the interface */
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  /** tabular mono — every figure (prices, rates, notionals) */
  mono: 'ui-monospace, "SF Mono", Menlo, "Cascadia Mono", Consolas, monospace',
} as const;

/**
 * Chrome bars (MenuBar / StatusBar / DlgTitle) — the Design ID masthead
 * treatment: ink ground with bone text and the accent as the signature rule.
 * Deliberately static across light/dark (ink chrome reads on both).
 */
export const HEADER_BAR = {
  bg: '#1B2230', // ink
  bgDeep: '#10151C',
  fg: '#ECE8DE', // bone
  fgSoft: '#A7A292',
} as const;

/**
 * Categorical chart ramp (recharts series order). Series 1 is always the
 * signature teal. Signed series (P&L, returns) use CHART_UP_DOWN instead.
 */
export const CHART_SERIES = ['#5FA892', '#3F4655', '#B08D52', '#7A5E72', '#8A8A55'] as const;
export const CHART_UP_DOWN = { up: LIGHT.up, down: LIGHT.down } as const;
